import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React from "react"
import { StyleSheet, TouchableOpacity, View } from "react-native"

/**
 * Reusable tag chips selector component
 *
 * @param {Array} tags - Array of tag objects with { key, label, emoji? } OR array of strings
 * @param {Array} selectedTags - Array of selected tag keys (or strings if tags are strings)
 * @param {Function} onTagToggle - Callback when a tag is toggled (receives the tag key or string)
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {boolean} small - Whether to use smaller chip sizes
 * @param {Object} style - Additional style for the container
 */
const TagChipsSelector = ({
    tags = [],
    selectedTags = [],
    onTagToggle,
    disabled = false,
    small = false,
    style,
}) => {
    // Normalize tag to object format - handles both string and object tags
    const normalizeTag = (tag) => {
        if (typeof tag === "string") {
            return { key: tag, label: tag, emoji: null }
        }
        return tag
    }

    const renderTagChip = (rawTag, index) => {
        const tag = normalizeTag(rawTag)
        const isSelected = selectedTags.includes(tag.key)

        return (
            <TouchableOpacity
                key={tag.key || index}
                onPress={() => onTagToggle?.(tag.key)}
                disabled={disabled}
                style={[
                    styles.tagChip,
                    small && styles.tagChipSmall,
                    isSelected && styles.tagChipSelected,
                    disabled && !small && styles.tagChipDisabled,
                ]}
                activeOpacity={0.7}
            >
                {tag.emoji && (
                    <TextM fontSize={small ? 10 : 14} style={styles.tagEmoji}>
                        {tag.emoji}
                    </TextM>
                )}
                <TextM
                    fontSize={small ? 10 : 13}
                    fontWeight={isSelected ? "SemiBold" : "Medium"}
                    color={
                        isSelected ? theme.colors.primary : theme.colors.grey
                    }
                >
                    {tag.label}
                </TextM>
            </TouchableOpacity>
        )
    }

    return (
        <View style={[styles.tagsRow, style]}>{tags.map(renderTagChip)}</View>
    )
}

const styles = StyleSheet.create({
    tagsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
    },
    tagChip: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 24,
        backgroundColor: theme.colors.white,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
    },
    tagChipSmall: {
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 12,
        borderWidth: 1,
    },
    tagChipSelected: {
        backgroundColor: theme.colors.primaryLighter,
        borderColor: theme.colors.primary,
        borderWidth: 2,
    },
    tagChipDisabled: {
        opacity: 0.7,
    },
    tagEmoji: {
        marginRight: 4,
    },
})

export default TagChipsSelector
